import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

/** Material-UI */
import Container from '@mui/material/Container';

/** API */
import apiFactory from 'services/api/axios';
import { getProductsByBrand } from 'api/product';

/** Utils */
import { getJwtFromCookie, getUserFromCookie } from 'utils/cookies';
import { log, replaceH1withH2, setPriceInDesiredCurrency } from 'utils/functions';

/** Hooks */
import useAuth from 'hooks/useAuth';
import { useTheme } from '@mui/material';
import useCurrency from 'hooks/useCurrency';
import { USE_COOKIES_QUEUE_KEYS, useCookieQueue } from 'hooks/useCookieQueue';
import { LOCAL_STORAGE_KEYS, useLocalStorageQueue } from 'hooks/useLocalStorageQueue';

/** Components */
import CBreadcrumbs from 'components/CBreadcrumbs';
import ProductDetail from 'components/ProductDetail';
import FullWidthLayout from 'layouts/FullWidthLayout';
import ProductPageSeo from 'components/Seo/ProductPageSeo';
import ReturnCTA from 'components/ProductDetail/ReturnCTA/ReturnBanner';
import MoreProductsFromThisBrand from 'components/Carousels/pdp/MoreProductsFromThisBrand';
import RecentBrowsedProductsCarousel from 'components/Carousels/common/RecentBrowsed/RecentBrowsedProductsCarousel';

/** Utils / Consts */
import routes from 'constants/routes';
import { logger } from 'services/api/logger';
import { productCarouselSettings } from 'utils/carousels';
import { NEXT_PUBLIC_BFF_URL } from 'constants/runtimeConfig';
import { GTMProductEvents, pushAlgoliaViewedObjectEvent } from 'utils/gtm';
import Page from 'components/Page';

const MAX_NUM_PRODUCTS_CAROUSEL = 16;

const productDefaultPropsProductData = {
  id: '',
  productThumbnailUrl: {
    productThumbnailName: '',
    productThumbnailBaseUrl: '',
  },
  productName: '',
  productLongDescription: '',
  productPublishedDate: '',
  productSlug: '',
  brandSlug: '',
  productPrice: [],
};

const productDefaultPropsInitialBreadcrumbs = [];

const Product = ({
  productData = productDefaultPropsProductData,
  initialBreadcrumbs = productDefaultPropsInitialBreadcrumbs,
}) => {
  const { asPath, pathname } = useRouter();
  const theme = useTheme();
  const router = useRouter();
  const { userId, user } = useAuth();
  const { currency, exchangeRates } = useCurrency();
  const { addToQueue } = useLocalStorageQueue(LOCAL_STORAGE_KEYS.PRODUCTS);
  const { addToCookiesQueue } = useCookieQueue(USE_COOKIES_QUEUE_KEYS.PRODUCTS);

  const [moreProducts, setMoreProducts] = useState([]);
  const [isLoadingMoreProducts, setIsLoadingMoreProducts] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getMoreProductsFromBrand = useCallback(
    async (brandSlug) => {
      setIsLoadingMoreProducts(true);
      try {
        const data = await getProductsByBrand(
          brandSlug,
          MAX_NUM_PRODUCTS_CAROUSEL + 1,
          undefined,
          user?.shippingCountry
        ); // +1 so we can remove the current product we are showing if needed

        // remove current product
        setMoreProducts(data?.products?.filter((x) => x.WP_id !== productData.WP_id));
      } catch (e) {
        log.error('Error on getProductsByBrand. Error: ', e);
      } finally {
        setIsLoadingMoreProducts(false);
      }
    },
    [productData.WP_id, user?.shippingCountry]
  );

  useEffect(() => {
    (async () => {
      if (!isEmpty(productData)) {
        await getMoreProductsFromBrand(productData?.brandSlug);
        addToQueue({ id: productData?.id, name: productData?.productName });
        addToCookiesQueue({ id: productData?.id, name: productData?.productName });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productData, getMoreProductsFromBrand]);

  // eslint-disable-next-line no-unused-vars
  const [selectedRRP, setSelectedRRP] = useState(0);
  const [selectedWSP, setSelectedWSP] = useState(0);

  // Effect to handle the selection of WSP and RRP based on currency and product data
  useEffect(() => {
    if (productData) {
      // get WSP in selected currency
      const wspInSelectedCurrency = productData?.productPrice
        ?.filter((x) => x?.productWSP > 0 && x?.productCurrency === currency?.name)
        .pop();

      if (wspInSelectedCurrency) {
        setSelectedWSP(Number(wspInSelectedCurrency.productWSP) / 100);
      } else {
        (async () => {
          setPriceInDesiredCurrency(
            productData?.productPrice,
            'productWSP',
            'productCurrency',
            currency?.name,
            setSelectedWSP,
            exchangeRates
          );
        })();
      }

      // get RRP in selected currency
      const rrpInSelectedCurrency = productData?.productPrice
        ?.filter((x) => x?.productRRP > 0 && x?.productCurrency === currency?.name)
        .pop();

      if (rrpInSelectedCurrency) {
        setSelectedRRP(Number(rrpInSelectedCurrency.productRRP) / 100);
      } else {
        (async () => {
          setPriceInDesiredCurrency(
            productData?.productPrice,
            'productRRP',
            'productCurrency',
            currency?.name,
            setSelectedRRP,
            exchangeRates
          );
        })();
      }
      setIsLoading(false);
    }
  }, [currency?.name, exchangeRates, productData]);

  // Effect to send product viewed GTM event
  useEffect(() => {
    if (productData) {
      const thumbnailUrl = `${productData?.productThumbnailUrl?.productThumbnailBaseUrl}/${productData?.productThumbnailUrl?.productThumbnailName}`;
      const productSlug = `/product/${productData?.productSlug}`;
      const brandSlug = `/store/${productData?.brandSlug}`;

      // Send GTM events based on userId and selectedWSP
      if (!userId || selectedWSP !== 0) {
        GTMProductEvents(
          productData?.productName,
          productData?.WP_id,
          selectedWSP,
          productData?.wholesaler?.name,
          thumbnailUrl,
          productSlug,
          brandSlug,
          currency?.name,
          'visit',
          userId
        );
        pushAlgoliaViewedObjectEvent('product', { objectID: productData?.id });
      }
    }
  }, [productData, selectedWSP, currency?.name, userId]);

  return (
    <Page
      sx={{
        [theme.breakpoints.down('sm')]: {
          mx: '-16px',
        },
      }}
    >
      <ProductPageSeo
        id={productData?.id}
        price={
          productData?.productPrice?.find((curr) => {
            return curr?.productCurrency === 'GBP';
          })?.productRRP
        }
        imageUrl={`https://${productData?.productThumbnailUrl?.productThumbnailBaseUrl}/${productData?.productThumbnailUrl?.productThumbnailName}`}
        url={`https://www.creoate.com${asPath}`}
        title={productData?.productName}
        canonical={`https://www.creoate.com${pathname}`}
        description={productData?.productLongDescription?.replace(/<[^>]+>/g, '')}
        modifiedTime={productData?.productPublishedDate}
        Brand={productData?.wholesaler?.name}
      />
      <Container
        maxWidth={false}
        sx={{
          pt: 1.25,
          [theme.breakpoints.down('md')]: {
            px: theme.spacing(1),
            pt: 1,
          },
        }}
      >
        <CBreadcrumbs
          sx={{ mb: 1, ml: 1 }}
          onBreadcrumbClick={(breadcrumb) => {
            if (breadcrumb.name === 'Creoate') {
              router?.replace('/');
            } else {
              router?.push(breadcrumb.slug);
            }
          }}
          initialBreadcrumbs={initialBreadcrumbs}
        />
        {/* Passing selectedWSP here to avoid recalculation later */}
        <ProductDetail product={productData} productWSP={selectedWSP} loading={isLoading} />
      </Container>

      <MoreProductsFromThisBrand
        products={moreProducts}
        isLoading={isLoadingMoreProducts}
        carouselSettings={productCarouselSettings}
      />

      <RecentBrowsedProductsCarousel type="products" viewAllRef="pdp" carouselSettings={productCarouselSettings} />

      <ReturnCTA />
    </Page>
  );
};

Product.getLayout = FullWidthLayout;

Product.propTypes = {
  productData: PropTypes.shape({
    id: PropTypes.string,
    WP_id: PropTypes.string,
    productThumbnailUrl: PropTypes.shape({
      productThumbnailName: PropTypes.string,
      productThumbnailBaseUrl: PropTypes.string,
    }),
    productName: PropTypes.string,
    productLongDescription: PropTypes.string,
    productPublishedDate: PropTypes.string,
    productSlug: PropTypes.string,
    brandSlug: PropTypes.string,
    productPrice: PropTypes.arrayOf(PropTypes.shape({})),
    wholesaler: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  initialBreadcrumbs: PropTypes.arrayOf(PropTypes.shape({})),
};

export const getServerSideProps = async ({ req }) => {
  let productSlug;
  let productResponse = null;

  let breadcrumbs = [{ name: 'Creoate', slug: '/', type: 'homepage' }];

  try {
    const userCountry = getUserFromCookie(req)?.shippingCountry;
    // cant use query nor params here because the slug may already be encoded....
    const proto = req.connection.encrypted ? 'https' : 'http';
    const baseURL = `${proto}://${req.headers.host}/`;
    const parsedUrl = new URL(req.url, baseURL);
    [productSlug] = parsedUrl.pathname.split('product/').pop().split('.'); // The url gets served with dot json sometimes

    const userCountryShippingCode = userCountry ? `&shippingCountryCode=${userCountry}` : '';
    const endpoint = `/products/${encodeURI(productSlug)}?${userCountryShippingCode}`;

    productResponse = (
      await apiFactory({
        baseURL: NEXT_PUBLIC_BFF_URL,
        headers: { Authorization: getJwtFromCookie(req) },
      }).get(endpoint)
    ).data;

    if (productResponse?.wholesaler.name && productResponse?.wholesaler.slug) {
      breadcrumbs.push({
        name: productResponse.wholesaler.name,
        slug: `/store/${productResponse.wholesaler.slug}`,
        type: 'brand',
      });
    }

    if (productResponse?.productLongDescription) {
      productResponse.productLongDescription = replaceH1withH2(productResponse.productLongDescription);
    }

    breadcrumbs = breadcrumbs.map((item) => {
      return { name: item.name, slug: item.slug, type: item.type };
    });

    if (productResponse?.productName && productResponse?.productSlug) {
      breadcrumbs.push({ name: productResponse.productName, slug: productResponse.productSlug, type: 'product' });
    }

    breadcrumbs = breadcrumbs.map((item) => {
      return { name: item.name, slug: item.slug, type: item.type };
    });
  } catch (err) {
    logger.error({ instance: 'product', type: 'getServerSideProps' }, err);
    // this is a "cant ship to your location" error
    if (err.response?.data?.errorCode === 'PRODUCT004') {
      return {
        redirect: {
          permanent: false,
          destination: routes.page.shippingRestricted,
        },
      };
    }
    return {
      redirect: {
        permanent: false,
        destination: routes.page.root,
      },
    };
  }

  return {
    props: {
      productData: productResponse,
      slug: productSlug,
      initialBreadcrumbs: breadcrumbs,
    },
  };
};

export default Product;
